import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FaPlus, FaTimes } from "react-icons/fa";

import visaLogo from "assets/Visa.svg";
import masterLogo from "assets/master.svg";

import InstallmentsTrigger from "./InstallmentsTrigger";

const BRAND_LOGOS = {
  Visa: visaLogo,
  Mastercard: masterLogo,
};

const Wrap = styled.div`
  margin-top: 24px;
  opacity: ${(p) => (p.$disabled ? 0.4 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
`;

const SectionLabel = styled.h3`
  font-size: 18px;
  font-weight: 400;
  color: var(--text-dark, #1D1D1F);
  margin: 0 0 12px;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  margin-bottom: 8px;
  background: ${(p) => (p.$selected ? "#DBEFE4" : "#FFF")};
  cursor: pointer;
`;

const Radio = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--button-primary-color, #1D1D1F);
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const RadioDot = styled.span`
  width: 12px;
  height: 12px;
  background: var(--button-primary-color, #1D1D1F);
  border-radius: 50%;
`;

const CardLabel = styled.span`
  flex: 1;
  font-size: 16px;
  color: var(--text-dark, #1D1D1F);
`;

const RemoveBtn = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f5f5f7;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 14px;
  background: #f5f5f7;
  border: 0;
  border-radius: 6px;
  font-size: 16px;
  color: var(--text-dark, #1D1D1F);
  cursor: pointer;
`;

const BrandLogo = styled.img`
  width: 36px;
  height: 22px;
  object-fit: contain;
`;

const BrandText = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

function BrandIcon({ brand }) {
  const logo = BRAND_LOGOS[brand];
  if (logo) return <BrandLogo src={logo} alt={brand} />;
  return brand ? <BrandText>{brand}</BrandText> : null;
}

BrandIcon.propTypes = {
  brand: PropTypes.string,
};

export default function CardSection({
  cards,
  selectedCardId,
  onSelectCard,
  onRemoveCard,
  onAddCard,
  installments,
  installmentsSelected,
  onPickInstallment,
  disabled,
}) {
  return (
    <Wrap $disabled={disabled}>
      <SectionLabel>Cartão de crédito</SectionLabel>
      {cards.map((card) => {
        const id = card.uuid;
        const isSelected = id === selectedCardId;
        const lastDigits = card.last_four_digits || "";
        const brand = card.brand || "";
        return (
          <Card
            key={id}
            $selected={isSelected}
            onClick={() => onSelectCard(id)}
          >
            <Radio type="button" tabIndex={-1} aria-label="Selecionar cartão">
              {isSelected && <RadioDot />}
            </Radio>
            <BrandIcon brand={brand} />
            <CardLabel>Final {lastDigits}</CardLabel>
            <RemoveBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveCard(id);
              }}
              aria-label="Remover cartão"
            >
              <FaTimes />
            </RemoveBtn>
          </Card>
        );
      })}
      <AddBtn type="button" onClick={onAddCard}>
        <FaPlus />
        Adicionar Cartão de crédito
      </AddBtn>

      <InstallmentsTrigger
        installments={installments}
        selected={installmentsSelected}
        onSelect={onPickInstallment}
        disabled={!selectedCardId || disabled}
      />
    </Wrap>
  );
}

CardSection.propTypes = {
  cards: PropTypes.array.isRequired,
  selectedCardId: PropTypes.string,
  onSelectCard: PropTypes.func.isRequired,
  onRemoveCard: PropTypes.func.isRequired,
  onAddCard: PropTypes.func.isRequired,
  installments: PropTypes.array.isRequired,
  installmentsSelected: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  onPickInstallment: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
